"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.StartEndable = exports.startEndDateAndTimesAttributes = void 0;
const LodashExtensions_1 = require("chaire-lib-common/lib/utils/LodashExtensions");
const ParamsValidatorUtils_1 = require("../../utils/ParamsValidatorUtils");
/**
 * Clock time `0` is midnight. Do not use `_isBlank` on seconds since midnight.
 */
const hasValidSecondsSinceMidnight = (secondsSinceMidnight) => typeof secondsSinceMidnight === 'number' && secondsSinceMidnight >= 0;
exports.startEndDateAndTimesAttributes = [
    'startDate',
    'startTime',
    'startTimePeriod',
    'endDate',
    'endTime',
    'endTimePeriod'
];
/**
 * A StartEndable is an object that has
 * a start and a end datetime and/or time period
 */
class StartEndable {
    /**
     * validates provided start and end date and time params
     * @param dirtyParams the params input
     * @param displayName the name of the object being validated
     * @returns Error[]
     */
    static validateParams(dirtyParams, displayName) {
        const errors = [];
        displayName = displayName ? displayName + ' StartEndable' : 'StartEndable';
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isDateString('startDate', dirtyParams.startDate, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('startTime', dirtyParams.startTime, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('startTimePeriod', dirtyParams.startTimePeriod, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isDateString('endDate', dirtyParams.endDate, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isPositiveInteger('endTime', dirtyParams.endTime, displayName));
        errors.push(...ParamsValidatorUtils_1.ParamsValidatorUtils.isString('endTimePeriod', dirtyParams.endTimePeriod, displayName));
        return errors;
    }
    /**
     * Whether the object has a start time or a start time period.
     * Midnight (`0`) counts; a blank period does not.
     * @param startEndable - Object with start/end times, or undefined
     * @returns true when startTime is a non-negative number or startTimePeriod is not blank
     */
    static hasStart(startEndable) {
        if (startEndable === undefined) {
            return false;
        }
        return hasValidSecondsSinceMidnight(startEndable.startTime) || !(0, LodashExtensions_1._isBlank)(startEndable.startTimePeriod);
    }
    /**
     * Whether the object has an end time or an end time period.
     * Midnight (`0`) counts; a blank period does not.
     * @param startEndable - Object with start/end times, or undefined
     * @returns true when endTime is a non-negative number or endTimePeriod is not blank
     */
    static hasEnd(startEndable) {
        if (startEndable === undefined) {
            return false;
        }
        return hasValidSecondsSinceMidnight(startEndable.endTime) || !(0, LodashExtensions_1._isBlank)(startEndable.endTimePeriod);
    }
    /**
     * checks if the start and end times are valid and coherent (end > start)
     * @param startEndable the start endable object
     * @returns true if the times are valid, false if not, undefined if the times are undefined
     */
    static timesAreValid(startEndable) {
        if (startEndable === undefined || startEndable.startTime === undefined || startEndable.endTime === undefined) {
            return undefined;
        }
        // times must numbers and be >= 0
        if (typeof startEndable.startTime !== 'number' ||
            typeof startEndable.endTime !== 'number' ||
            isNaN(startEndable.startTime) ||
            isNaN(startEndable.endTime) ||
            startEndable.startTime < 0 ||
            startEndable.endTime < 0) {
            return false;
        }
        // if dates are set: make sure times are valid with dates:
        if (startEndable.startDate && startEndable.endDate) {
            // convert JS date timestamp to seconds (it is in milliseconds)
            const startTimestamp = new Date(startEndable.startDate).getTime() / 1000 + startEndable.startTime;
            const endTimestamp = new Date(startEndable.endDate).getTime() / 1000 + startEndable.endTime;
            return startTimestamp <= endTimestamp;
        }
        else {
            // if no dates are set, make sure times are coherent
            return startEndable.startTime <= startEndable.endTime;
        }
    }
    /**
     * Get the duration of the object in seconds
     * endTime must be >= startTime and both must exist
     * @returns {Optional<number>} - Returns the duration in seconds, or undefined if no start or end time
     */
    static getDurationSeconds(startEndable) {
        if (startEndable &&
            startEndable.startTime !== undefined &&
            startEndable.endTime !== undefined &&
            this.timesAreValid(startEndable)) {
            if (startEndable.startDate && startEndable.endDate) {
                // convert JS date timestamp to seconds (it is in milliseconds)
                const startTimestamp = new Date(startEndable.startDate).getTime() / 1000 + startEndable.startTime;
                const endTimestamp = new Date(startEndable.endDate).getTime() / 1000 + startEndable.endTime;
                return endTimestamp - startTimestamp;
            }
            else {
                const startTimestamp = startEndable.startTime;
                const endTimestamp = startEndable.endTime;
                return endTimestamp - startTimestamp;
            }
        }
        return undefined;
    }
}
exports.StartEndable = StartEndable;
//# sourceMappingURL=StartEndable.js.map